import { BaseApiClient } from './base-client';

export interface NFSShare {
  path: string;
  source_ip: string;
  read_write: boolean;
  description?: string;
  status?: string;
  share?: string;  // Some endpoints might return this
  permissions?: string;
  allowed_ips?: string[];
  name?: string;
}

export interface NFSShareCreate {
  share: string;  // Backend expects 'share' not 'path'
  source_ip: string;
  permissions: string;  // Backend expects "rw" or "ro" as string
}

export interface NFSShareUpdate {
  permissions?: string;
  source_ip?: string;
  read_write?: boolean;
}

export interface NFSStatus {
  status: string;
  status_nfsd: string;
  status_mountd: string;
  active_exports: number;
  connected_clients: number;
  nfs_version: string[];
  threads: number;
  uptime: string;
}

export interface NFSClient {
  ip_address: string;
  hostname?: string;
  mount_point: string;
  nfs_version: string;
  connected_since: string;
  last_activity: string;
  read_bytes: number;
  write_bytes: number;
}

export interface NFSStats {
  total_read_ops: number;
  total_write_ops: number;
  total_read_bytes: number;
  total_write_bytes: number;
  cache_hits: number;
  cache_misses: number;
  active_connections: number;
}

export class NFSApiClient extends BaseApiClient {
  // Share management
  async listShares(serviceId: number): Promise<NFSShare[]> {
    return this.get<NFSShare[]>(`/api/v1/nfs/${serviceId}/shares`);
  }

  async createShare(serviceId: number, share: NFSShareCreate): Promise<NFSShare> {
    return this.post<NFSShare>(`/api/v1/nfs/${serviceId}/shares`, share);
  }

  async updateShare(serviceId: number, path: string, updates: NFSShareUpdate): Promise<NFSShare> {
    return this.patch<NFSShare>(`/api/v1/nfs/${serviceId}/shares/${encodeURIComponent(path)}`, updates);
  }

  async deleteShare(serviceId: number, path: string): Promise<{ status: string }> {
    return this.delete<{ status: string }>(`/api/v1/nfs/${serviceId}/shares/${encodeURIComponent(path)}`);
  }

  // Client management
  async listClients(serviceId: number): Promise<NFSClient[]> {
    return this.get<NFSClient[]>(`/api/v1/nfs/${serviceId}/clients`);
  }

  async disconnectClient(serviceId: number, clientIp: string): Promise<{ status: string }> {
    return this.post<{ status: string }>(`/api/v1/nfs/${serviceId}/clients/${clientIp}/disconnect`);
  }

  // Status and stats
  async getStatus(serviceId: number): Promise<NFSStatus> {
    return this.get<NFSStatus>(`/api/v1/nfs/${serviceId}/status`);
  }

  async getStats(serviceId: number): Promise<NFSStats> {
    return this.get<NFSStats>(`/api/v1/nfs/${serviceId}/stats`);
  }

  // Service control
  async reloadService(serviceId: number): Promise<{ status: string }> {
    return this.post<{ status: string }>(`/api/v1/nfs/${serviceId}/reload`);
  }

  async syncShares(serviceId: number): Promise<{ status: string; shares_synced: number }> {
    return this.post<{ status: string; shares_synced: number }>(`/api/v1/nfs/${serviceId}/sync`);
  }
}